import { Component, Element, Fragment, Listen, Prop, State, Watch, getAssetPath, h } from '@stencil/core';
import state from '../../store';
import { Business, Product, WidgetStyle } from '../../types';
import { getCustomer, getGreenDiscount, getOptions, getRot, getRut, loginCustomer } from '../../utils/api';
import { getProductPriceWithRotAndRut } from '../../utils/calc';
import { scrollToTop } from '../../utils/scroll';

@Component({
  tag: 'hemfixarna-component',
  styleUrl: 'hemfixarna.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class HemfixarnaComponent {
  @State() modal: boolean = false;
  @State() showModal: boolean = false;
  @State() product: Product | null = null;
  @Prop() id?: string;
  @Prop() slug?: string;
  @Prop() business: Business;
  @Prop() widgetStyle: WidgetStyle = WidgetStyle.standard;
  @Prop() buttonColor?: string;
  @Prop() buttonBg?: string;
  @State() proppedProduct: Product | null = null;
  @State() loginSourceError: null | string = null;

  @Element() private el: HTMLElement;

  loadCategoryOrProduct(id: string) {
    this.proppedProduct = null;
    if (id.startsWith('c-')) {
      const categories = [...state.customer.categories, ...state.customer.categories.map(c => c.sub_categories ?? [])].flat();
      const category = categories.find(c => c && c.id === id.replace('c-', ''));
      if (category) {
        state.selectedCustomerCategory = category;
        state.step = 2;
      }
    } else {
      const products = state.customer.categories
        .map(c => (c.show_products ? c.products : c.sub_categories ? c.sub_categories.map(c => c.products) : []))
        .flat()
        .flat();
      const product = products.find(p => p && p.fields.ID === parseInt(id));
      if (product) {
        state.selectedProduct = product.fields;
        state.step = 4;
        this.proppedProduct = product.fields;
      }
    }
  }

  @Watch('id')
  async watchIdChange(id: string) {
    if (id && state.customer) {
      if (id === 'maleri') {
        state.maleri = true;
        state.step = 4;
      } else {
        this.loadCategoryOrProduct(id);
      }
    }
  }

  async componentWillLoad() {
    state.business = this.business;
    const cart = window.sessionStorage.getItem(`hemfixarna-${this.business}-cart`);
    if (cart) {
      state.cart = JSON.parse(cart);
    }
    const user = window.sessionStorage.getItem(`hemfixarna-${this.business}-user`);
    if (user) {
      state.user = JSON.parse(user);
    }
    const creditSafeUser = window.sessionStorage.getItem(`hemfixarna-${this.business}-creditSafeUser`);
    if (creditSafeUser) {
      state.creditSafeUser = JSON.parse(creditSafeUser);
    }

    const [options, rut, rot, greenDiscount] = await Promise.all([getOptions(), getRut(), getRot(), getGreenDiscount()]);

    try {
      const customer = await getCustomer(this.business);
      if (customer?.code === 'not_found') {
        console.warn('customer not found');
      } else if (customer) {
        state.customer = customer;
        const id = this.id || this.slug;
        if (id) {
          if (id === 'maleri') {
            state.maleri = true;
            state.step = 4;
          } else {
            this.loadCategoryOrProduct(id);
          }
        }
      }
    } catch (error) {
      console.warn('customer not found');
    }

    state.rotOptions = rot;
    state.rutOptions = rut;
    state.greenOptions = greenDiscount;
    state.options = options;

    if (localStorage.getItem('select_source')) {
      state.selectSource = true;
    }
  }

  triggerScrollTotop = () => {
    const el = this.el.shadowRoot.querySelector('.hemfixarna_content');
    scrollToTop(el);
  };

  // Open close modal && click outside event
  openModal() {
    if (this.id) {
      if (this.id === 'maleri') {
        state.maleri = true;
        state.step = 4;
      } else {
        state.maleri = false;
        this.loadCategoryOrProduct(this.id);
      }
    }
    this.modal = true;
    setTimeout(() => {
      this.showModal = true;
    }, 50);
  }
  closeModal() {
    this.showModal = false;
    setTimeout(() => {
      this.modal = false;
    }, 200);
  }
  @Listen('click')
  handleClick(e: Event) {
    if ((e?.composedPath()[0] as HTMLElement).classList.contains('hemfixarna_backdrop')) {
      this.closeModal();
    }
    const infomodal = this.el.shadowRoot.querySelector('.hemfixarna_infomodal');
    if (infomodal) {
      const isClickInside = infomodal.contains(e.composedPath()[0] as HTMLInputElement);
      if (!isClickInside) {
        state.modal = null;
      }
    }
  }

  async handleLogin() {
    this.loginSourceError = null;
    const passwordInput = this.el.shadowRoot.querySelector('input[type="password"]');
    const password = (passwordInput as HTMLInputElement).value;

    try {
      const status = await loginCustomer({ id: state.customer.ID, password: password });
      if (status === 200) {
        state.showSourcePasswordPrompt = false;
        state.selectSource = true;
      } else {
        this.loginSourceError = 'Fel lösenord';
      }
    } catch (error) {
      console.log(error);
      this.loginSourceError = 'Fel lösenord';
    }
  }

  getCartLength() {
    return state.cart.reduce((acc, curr) => acc + curr.amount, 0);
  }

  render() {
    const logo = getAssetPath(`./assets/hemfixarna.svg`);
    const pensel = getAssetPath(`./assets/pensel.svg`);
    const monteringLogo = getAssetPath(`./assets/montering.svg`);

    return (
      <div class={`hemfixarna hemfixarna-${this.business}`}>
        <div class={`hemfixarna_box hemfixarna_box--${this.widgetStyle}`}>
          {[WidgetStyle.alternative_2, WidgetStyle.alternative_3].includes(this.widgetStyle) ? (
            <img src={this.id === 'maleri' ? pensel : monteringLogo} alt="montering logo" width={32} height={32} />
          ) : null}
          <div>
            <div>
              <p
                onClick={() => this.openModal()}
                class={`pointer ${[WidgetStyle.alternative_2, WidgetStyle.alternative_3].includes(this.widgetStyle) && state.selectedProduct ? 'underline' : ''}`}
              >
                {this.id === 'maleri' ? (
                  <span>Beräkna fast pris på måleri & tapetsering här</span>
                ) : (
                  <Fragment>
                    {!state.selectedCustomerCategory && !this.product && !this.proppedProduct ? (
                      <Fragment>
                        {state.customer?.widget_title ? (
                          <span>{state.customer.widget_title}</span>
                        ) : (
                          <span>
                            Montering/Installation - <strong class="underline">se priser här</strong>
                          </span>
                        )}
                      </Fragment>
                    ) : (
                      <Fragment>
                        {this.product?.title || this.proppedProduct?.title || state.selectedCustomerCategory?.widget_title || (
                          <span>
                            Montering
                            <wbr />
                            /Installation {state.selectedCustomerCategory?.name ?? 'på plats'} - <strong class="underline">se priser här</strong>
                          </span>
                        )}
                        {this.proppedProduct && !this.product?.invoice && !this.proppedProduct?.invoice ? (
                          <span>
                            {' från'} <strong>{getProductPriceWithRotAndRut(this.proppedProduct || this.product)}kr</strong>
                          </span>
                        ) : null}
                      </Fragment>
                    )}
                  </Fragment>
                )}
              </p>
            </div>
            {this.widgetStyle === WidgetStyle.standard ? <img src={logo} width={104} /> : null}
            {this.widgetStyle === WidgetStyle.alternative ? (
              <span class="p-s">
                Utförs av <strong>Hemfixarna</strong>
              </span>
            ) : null}
          </div>
          {[WidgetStyle.standard, WidgetStyle.alternative].includes(this.widgetStyle) ? (
            <button
              onClick={() => this.openModal()}
              class="hemfixarna_btn"
              style={{
                color: this.buttonColor?.startsWith('#') || !this.buttonColor?.length ? this.buttonColor : `#${this.buttonColor}`,
                backgroundColor: this.buttonBg?.startsWith('#') || !this.buttonBg?.length ? this.buttonBg : `#${this.buttonBg}`,
              }}
            >
              Beställ här
              {this.getCartLength() > 0 && <span>{this.getCartLength()}</span>}
            </button>
          ) : null}
          {WidgetStyle.alternative_2 === this.widgetStyle ? (
            <div class="hemfixarna_altbtn">
              <strong class="p-s">Utförs av</strong>
              <img src={logo} alt="hemfixarna logo" width={98} />
            </div>
          ) : null}
        </div>
        {this.modal && (
          <div>
            <div class={`hemfixarna_modal ${this.showModal ? 'hemfixarna_modal--open' : ''}`}>
              {state.modal && (
                <div class="hemfixarna_infomodal">
                  {state.modal.title && <h2>{state.modal.title}</h2>}
                  <p innerHTML={state.modal.text}></p>
                  <div>
                    <button onClick={() => (state.modal = null)}>Stäng</button>
                  </div>
                </div>
              )}
              {state.showSourcePasswordPrompt ? (
                <div class="hemfixarna_source--pw hemfixarna_infomodal">
                  <form
                    onSubmit={e => {
                      e.preventDefault();
                      this.handleLogin();
                    }}
                  >
                    <input type="password" placeholder="Lösenord" />
                    <input type="submit" value="Logga in" />
                    {this.loginSourceError && <span>Fel lösenord</span>}
                  </form>
                </div>
              ) : null}
              {state.customer && <hemfixarna-breadcrumbs closeModal={() => this.closeModal()} />}
              <div class={`hemfixarna_content hemfixarna_content--${state.step} ${state.step === 4 && state.maleri ? 'hemfixarna_content--painting' : ''}`}>
                {state.step < 4 && <hemfixarna-start />}
                {/* // IF SELECTED PRODUCT (step 4) */}
                {state.step === 4 && <hemfixarna-product />}
                {/* // IF CHECKOUT (step 5) */}
                {state.step === 5 && <hemfixarna-cart />}
                {/* // IF ORDER CONFIRM (step 6) */}
                {state.step === 6 && <hemfixarna-order />}
              </div>
            </div>
            <div class={`hemfixarna_backdrop ${this.showModal ? 'hemfixarna_backdrop--open' : ''}`}></div>
          </div>
        )}
      </div>
    );
  }
}
